import { fmtMoney, fmtSigned, plural, EMPTY_LABELS } from "./format";

// The book — the portfolio as a quote board (R1-OD-12/13/22). One row per building, one column per
// rung. Every cell shows the value and the change since THAT number last moved (never a cross-rung
// change). The mark rung is the delta-cell the founder most wanted audited: the numeral is tinted
// red/green, the CELL is never tinted. Aggregates are derived from the rows shown, never literals.

function Delta({ change }) {
  if (change == null) return null;
  if (change === 0) return <span className="cell-nochange">· no change</span>;
  const down = change < 0;
  // numeral + arrow glyph ONLY — this span is the only thing that carries directional colour.
  return (
    <span className={"cell-delta " + (down ? "delta-down" : "delta-up")}
          data-testid="cell-delta">
      {down ? "▼" : "▲"} {fmtSigned(change)}
    </span>
  );
}

function Cell({ cell, testid }) {
  if (cell.conflict) {
    return <td className="book-cell" data-testid={testid}>
      <span className="cell-status-red" data-testid="cell-conflict">claim conflict</span>
    </td>;
  }
  if (cell.undefendable) {
    return <td className="book-cell" data-testid={testid}>
      <span className="cell-needs" data-testid="cell-undefendable">undefendable · needs you</span>
    </td>;
  }
  if (cell.value != null) {
    return <td className="book-cell" data-testid={testid}>
      <span className="cell-value">{fmtMoney(cell.value)}</span>
      <Delta change={cell.change_since_last_move} />
      {/* F107: a re-read never takes the book to zero — the standing mark stays, with a quiet line.
          F121: in the design's live voice — the agent, the pulsing dot, and WHY. */}
      {cell.re_reading && (
        <span className="cell-rereading" data-testid="cell-re-reading">
          <span className="v4-work" data-testid="cell-re-reading-agent">
            <span className="v4-work-dot" />{cell.re_reading_agent}
          </span>
          <span className="v4-work-line">{cell.re_reading_line}</span>
        </span>
      )}
    </td>;
  }
  const meta = EMPTY_LABELS[cell.empty] || { text: "—", tone: "neutral" };
  const cls = meta.tone === "amber" ? "cell-needs" : "cell-empty";
  // Step D ①: a file in flight renders its per-state filing copy from t=0 (never a bare "—").
  const shown = cell.state === "reading" && cell.message ? cell.message : meta.text;
  return <td className="book-cell" data-testid={testid}>
    <span className={cls} title={cell.message || undefined}
          data-testid={cell.state ? "cell-state-" + cell.state : undefined}>{shown}</span>
  </td>;
}

function Disposition({ value }) {
  if (!value) return <span className="disp disp-none" data-testid="disp-none">not yet asked</span>;
  const label = value === "selling" ? "ACTIVELY SELLING" : "OPEN TO PRICE";
  return <span className={"disp disp-" + value} data-testid={"disp-" + value}>{label}</span>;
}

export function Book({ book, onOpen }) {
  const { rows, summary } = book;
  return (
    <div className="book" data-testid="book">
      <table className="book-table">
        <thead>
          <tr>
            <th className="book-th book-th-left">Building</th>
            <th className="book-th book-th-left">Disposition</th>
            <th className="book-th">Your number</th>
            <th className="book-th">The mark</th>
            <th className="book-th">Matched</th>
            <th className="book-th">Structured</th>
            <th className="book-th">Closed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const mk = r.rungs.mark || {};
            // F83 (founder 28 Sep): the in-flight line belongs ONLY to a building with a job actually
            // queued or running. `upstream_incomplete` is not that — it was animating a row forever.
            const inFlight = mk.working === true || mk.empty === "reading";
            return (
            <tr key={r.building_ref} className={"book-row" + (inFlight ? " is-reading" : "")} data-testid={"book-row-" + r.building_ref}
                onClick={() => onOpen(r.building_ref)}>
              <td className="book-cell book-cell-left">
                <span className="book-ref" data-testid={"book-name-" + r.building_ref}>
                  {r.name || r.address || r.building_ref}
                  {r.unseen && <span className="book-new" data-testid={"book-new-" + r.building_ref}
                                     title="New since you last looked">•</span>}
                </span>
                {r.name && r.address && <span className="book-sub">{r.address}</span>}
              </td>
              <td className="book-cell book-cell-left"><Disposition value={r.disposition} /></td>
              <Cell cell={r.rungs.your_number} testid={"cell-your-" + r.building_ref} />
              <Cell cell={r.rungs.mark} testid={"cell-mark-" + r.building_ref} />
              <Cell cell={r.rungs.matched} testid={"cell-matched-" + r.building_ref} />
              <Cell cell={r.rungs.structured} testid={"cell-structured-" + r.building_ref} />
              <Cell cell={r.rungs.closed} testid={"cell-closed-" + r.building_ref} />
            </tr>
            );
          })}
        </tbody>
      </table>
      <div className="book-foot" data-testid="book-summary">
        <span>{plural(summary.buildings_owned, "building")}</span>
        <span className="book-foot-sep">·</span>
        <span>{summary.marked_count} marked</span>
        <span className="book-foot-sep">·</span>
        <span>
          {summary.portfolio_value != null
            ? <>portfolio value <strong>{fmtMoney(summary.portfolio_value)}</strong></>
            : "nothing marked yet"}
        </span>
        <span className="book-foot-note">blends exclude unmarked buildings; the count includes them</span>
      </div>
    </div>
  );
}
